import { FeatureFlags, LogosMap, SiteExperienceConfig } from '@draftkings/sportsbook-common-contracts';
import type { LocalizationConfig } from '@draftkings/sportsbook-labels';
import { getDefaultValue } from '@draftkings/sportsbook-common-utils';
import type * as ContractTypes from '@draftkings/dk-data-layer';
import { formatDate } from '../formatDate/formatDate';
import { parseLiveGameState } from '../parseLiveGameState';
import { isEventStarted } from '../../../helpers/isEventStarted/isEventStarted';
import { getTeamLogoUrl } from '../getTeamLogoUrl';
import { dict } from '../../../helpers';

type PartialParsedInputsType = {
    event: ContractTypes.SportEvent;
    isTeamSwap: boolean;
    logosMap: LogosMap;
    staticS3Host: string;
    productConfig: SiteExperienceConfig;
    featureFlags: FeatureFlags;
    renderEventNavLink: (props: { children: React.ReactNode }) => JSX.Element;
    renderSGPNavLink: (props: { children: React.ReactNode }) => JSX.Element;
    sgpBadgeAriaLabel?: string;
    isParlayCardComponent: boolean;
    locale: string;
    localizationConfig: LocalizationConfig;
};

export const getPartialParsedInputs = ({
    event,
    isTeamSwap,
    logosMap,
    staticS3Host,
    productConfig,
    featureFlags,
    renderEventNavLink,
    renderSGPNavLink,
    sgpBadgeAriaLabel,
    isParlayCardComponent,
    locale,
    localizationConfig
}: PartialParsedInputsType) => {
    if (isParlayCardComponent) {
        return {
            marketData: {
                startEventDate: event.startEventDate,
                parsedEventState: parseLiveGameState(event.liveGameState, event.status),
                tags: getDefaultValue(event.tags, [])
            },
            headerData: {
                title: formatDate(event.startEventDate, isParlayCardComponent, locale)
            }
        };
    }

    const teamName1 = event.participants?.[0]?.name || '';
    const teamName2 = event.participants?.[1]?.name || '';
    const team1LogoUrl = getTeamLogoUrl({
        participant: event.participants?.[0],
        logos: logosMap?.Teams || {},
        productConfig,
        staticS3Host,
        featureFlags
    });
    const team2LogoUrl = getTeamLogoUrl({
        participant: event.participants?.[1],
        logos: logosMap?.Teams || {},
        productConfig,
        staticS3Host,
        featureFlags
    });

    const isOutright =
        !event.eventParticipantType ||
        event.eventParticipantType === 'MultiTeam' ||
        event.eventParticipantType === 'MultiPlayer';
    const hasTeams = Boolean(teamName1 && teamName2);
    const title = !hasTeams ? event.name : undefined;

    if (!isOutright && hasTeams) {
        const rightSide = isEventStarted(event) ? '' : formatDate(event.startEventDate, isParlayCardComponent, locale);
        const separator = isTeamSwap
            ? localizationConfig?.livePage.MiddleTermAt || dict.MiddleTermAt
            : localizationConfig?.livePage.MiddleTermVs || dict.MiddleTermVs;

        return {
            marketData: {
                tags: getDefaultValue(event.tags, [])
            },
            headerData: {
                title,
                rightSide,
                separator,
                teamName1,
                teamName2,
                team1LogoUrl,
                team2LogoUrl,
                renderEventNavLink,
                renderSGPNavLink,
                sgpBadgeAriaLabel
            }
        };
    }

    return {
        marketData: {
            startEventDate: event.startEventDate,
            parsedEventState: parseLiveGameState(event.liveGameState, event.status)
        },
        headerData: {
            title: event.name,
            renderEventNavLink
        }
    };
};
